/**
 * Create Or Edit Chart Tool Schema
 * 
 * Create a new chart or edit an existing chart on a slide.
 * Uses categoryOperations and seriesOperations for data changes.
 * 
 * Features:
 * - Per-property inheritance via styledProperty() wrapper
 * - chartArea styling for entire chart container
 * - dataPoints per-point overrides within series
 * - PPT-friendly data operations: setValues, extendValues, insertValuesAt
 */

import { z } from "zod";

// Import shared schemas
import {
    PositionSchema,
    FillSchema,
    OutlineSchema,
    ChartTypeEnum,
    DataGeneratorSchema,
    SeriesSchema,
    AxisSchema,
} from "../../../tools/liveBuildArea/liveBuildArea.schema";

import { MetadataOperationsSchema } from "../createOrEditShape/createOrEditShape.schema";

// ═══════════════════════════════════════════════════════════════════════════
// INHERITANCE INFRASTRUCTURE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PPT inheritance reference - reference another chart to inherit properties from
 */
export const InheritFromRefSchema = z.object({
    chartShapeId: z.number().int()
        .describe("Shape ID of source chart"),
    slideId: z.number().int().optional()
        .describe("Slide containing source chart (default: same slide)"),
    seriesIndex: z.number().int().min(0).optional()
        .describe("For series-level properties: which series to inherit from"),
    pointIndex: z.number().int().min(0).optional()
        .describe("For point-level properties: which data point to inherit from (0-based)"),
});

/**
 * Helper function to create a styled property that can be direct value OR inherited.
 * This enables per-property inheritance from another chart.
 * 
 * Usage:
 *   fill: styledProperty(FillSchema).optional()
 * 
 * Allows:
 *   fill: { color: "#FF0000" }           // Direct value
 *   fill: { inheritFrom: { chartShapeId: 42 } }  // Inherited
 */
function styledProperty<T extends z.ZodTypeAny>(schema: T) {
    return z.union([
        schema,
        z.object({ inheritFrom: InheritFromRefSchema }),
    ]);
}

// ═══════════════════════════════════════════════════════════════════════════
// TEXT FORMAT SCHEMA (shared across multiple components)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Text formatting schema - used by data labels, legend, title, etc.
 */
export const TextFormatSchema = z.object({
    fontFamily: z.string().optional()
        .describe("Font family name (e.g., 'Arial', 'Calibri')"),
    fontSize: z.number().optional()
        .describe("Font size in points"),
    bold: z.boolean().optional()
        .describe("Bold text"),
    italic: z.boolean().optional()
        .describe("Italic text"),
    underline: z.boolean().optional()
        .describe("Underline text"),
    color: z.string().optional()
        .describe("Text color (hex)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// MARKER SCHEMA (for line/scatter charts)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Marker style enum for line/scatter chart points
 * 
 * NOTE: "dash" and "dot" are HORIZONTAL BAR shapes (outline only, no fill):
 * - "dash" = long horizontal bar (xlMarkerStyleDash)
 * - "dot" = short horizontal bar (xlMarkerStyleDot) - NOT a round dot!
 * - For a round dot, use "circle" with small size
 * 
 * These bar-style markers only show their outline color, fill has no effect.
 */
export const MarkerStyleEnum = z.enum([
    "automatic", "circle", "square", "diamond", "triangle", "star",
    "x", "plus", "dash", "dot", "none",
]);

/**
 * Marker schema for line/scatter chart data points
 * 
 * NOTE: For "dash" and "dot" marker styles, only outline.color is visible 
 * (they are outline-only shapes). fill has no visual effect on these styles.
 */
export const MarkerSchema = z.object({
    style: MarkerStyleEnum.optional()
        .describe("Marker shape. NOTE: 'dash' and 'dot' are horizontal bars, not round dots. Use 'circle' for round markers."),
    size: z.number().int().min(2).max(72).optional()
        .describe("Marker size in points (2-72)"),
    fill: FillSchema.optional()
        .describe("Marker fill. NOTE: Has no effect on 'dash' or 'dot' styles (outline-only shapes)."),
    outline: OutlineSchema.optional()
        .describe("Marker outline/border. This is the ONLY visible property for 'dash'/'dot' styles."),
});

// ═══════════════════════════════════════════════════════════════════════════
// DATA POINT OVERRIDE SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Data label schema for individual points
 */
export const DataLabelSchema = z.object({
    show: z.boolean().optional()
        .describe("Show data label for this point"),
    position: z.enum(["center", "inside_end", "inside_base", "outside_end", "left", "right", "top", "bottom", "above", "below", "best_fit"]).optional()
        .describe("Label position"),
    formatCode: z.string().optional()
        .describe("Number format (e.g., '#,##0', '0.00%')"),
    textFormat: TextFormatSchema.optional(),
});

/**
 * Per-point style override within a series.
 * Used for highlighting specific bars, pie slices, or markers.
 */
export const DataPointOverrideSchema = z.object({
    index: z.number().int().min(0)
        .describe("Data point index (0-based)"),
    fill: styledProperty(FillSchema).optional()
        .describe("Override fill for this point"),
    outline: styledProperty(OutlineSchema).optional()
        .describe("Override outline for this point"),
    marker: styledProperty(MarkerSchema).optional()
        .describe("Override marker for this point (line/scatter charts)"),
    dataLabel: styledProperty(DataLabelSchema).optional()
        .describe("Data label for this specific point"),
    explosion: z.number().int().min(0).max(400).optional()
        .describe("Pie slice explosion for this point (0-400)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// CHART AREA SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Chart area schema - styles the entire chart container (background/border)
 * This is distinct from plotArea which is just the data region.
 */
export const ChartAreaSchema = z.object({
    fill: z.object({
        color: z.string().optional()
            .describe("Background color (hex)"),
        transparency: z.number().min(0).max(1).optional()
            .describe("Transparency (0=opaque, 1=fully transparent)"),
    }).optional()
        .describe("Chart background. DEFAULT: transparent."),
    outline: z.object({
        show: z.boolean().optional()
            .describe("Show border. DEFAULT: false (OFF)."),
        color: z.string().optional()
            .describe("Border color (hex)"),
        weight: z.number().optional()
            .describe("Border weight in points"),
        dashStyle: z.enum(["solid", "dash", "dot", "dash_dot", "dash_dot_dot"]).optional()
            .describe("Border dash style"),
    }).optional()
        .describe("Chart border. DEFAULT: none."),
});

// ═══════════════════════════════════════════════════════════════════════════
// SERIES STYLE SCHEMA (with dataPoints)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Series style schema for applying styling to individual series.
 * Includes dataPoints for per-point overrides.
 */
export const SeriesStyleSchema = z.object({
    index: z.number().int().min(0)
        .describe("Which series to style (0-based)"),
    
    // === COMBO CHART: Per-series chart type ===
    chartType: ChartTypeEnum.optional()
        .describe("Per-series chart type for combo charts (e.g., column + line)"),
    
    // === DUAL AXIS: Primary or secondary axis ===
    axisGroup: z.enum(["primary", "secondary"]).optional()
        .describe("Which Y-axis to plot against. Use 'secondary' for dual-axis charts."),
    
    // === LINE SMOOTHING: Per-series override ===
    smooth: z.boolean().optional()
        .describe("Enable smooth/curved lines for this series (line charts only)"),
    
    // Styling - each can be direct OR inherited
    fill: styledProperty(FillSchema).optional()
        .describe("Series fill (bars, area, pie slices)"),
    outline: styledProperty(OutlineSchema).optional()
        .describe("Series outline/border"),
    line: styledProperty(OutlineSchema).optional()
        .describe("Line styling for line/area charts"),
    marker: styledProperty(MarkerSchema).optional()
        .describe("Marker styling for line/scatter charts"),
    dataLabels: styledProperty(DataLabelSchema).optional()
        .describe("Data labels for all points in this series"),
    
    // Per-point overrides
    dataPoints: z.array(DataPointOverrideSchema).optional()
        .describe("Per-point style overrides (highlight specific bars/slices)"),
    
    // Chart-type specific per-series options
    explosion: z.number().int().min(0).max(400).optional()
        .describe("Pie slice explosion (0-400, default: 0)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORY OPERATIONS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Category operation schema
 * 
 * Operations:
 * - insert: Add category at specific index
 * - remove: Remove category at specific index
 * - setValues: Replace ALL categories with new values
 * - extendValues: Append categories to the end
 */
export const CategoryOperationSchema = z.object({
    operation: z.enum(["insert", "remove", "setValues", "extendValues"])
        .describe("Operation type"),
    
    index: z.number().int().min(0).optional()
        .describe("Category index (for insert/remove)"),
    
    value: z.union([z.string(), z.number()])
        .optional()
        .describe("Single category value (for insert)"),
    
    values: z.array(z.union([z.string(), z.number()])).optional()
        .describe("Category values (setValues: replace all, extendValues: append)"),
    
    spacingConsequences: z.string().optional()
        .describe("Impact: 'Adding category may widen chart if X-axis labels get longer'"),
    
    shapesAffectedBySpacingConsequences: z.array(z.number().int()).optional()
        .describe("Shape IDs affected by chart dimension changes"),
}).refine(
    (data) => {
        switch (data.operation) {
            case "insert":
                return data.index !== undefined && data.value !== undefined;
            case "remove":
                return data.index !== undefined;
            case "setValues":
            case "extendValues":
                return data.values !== undefined && data.values.length > 0;
            default:
                return true;
        }
    },
    {
        message: "insert requires index+value, remove requires index, setValues/extendValues require values array",
    }
);

// ═══════════════════════════════════════════════════════════════════════════
// SERIES OPERATIONS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Series operation schema
 * 
 * Operations:
 * - insert: Add NEW series at index with values
 * - remove: Remove series at index
 * - replace/setValues: Replace ALL values for a series
 * - move: Reorder series position
 * - extendValues: Append values to end of series
 * - insertValuesAt: Insert values at specific index within series
 */
export const SeriesOperationSchema = z.object({
    operation: z.enum(["insert", "remove", "replace", "move", "setValues", "extendValues", "insertValuesAt"])
        .describe("Operation type"),
    
    index: z.number().int().min(0).optional()
        .describe("Series index (for most operations)"),
    
    name: z.string().optional()
        .describe("Series name (for insert/replace)"),
    
    values: z.array(z.union([z.number(), z.null()])).optional()
        .describe("Series values (for insert/replace/setValues)"),
    
    // For extendValues operation
    appendValues: z.array(z.union([z.number(), z.null()])).optional()
        .describe("Values to append (for extendValues)"),
    
    // For insertValuesAt operation
    atIndex: z.number().int().min(0).optional()
        .describe("Position to insert values within series (for insertValuesAt)"),
    
    insertValues: z.array(z.union([z.number(), z.null()])).optional()
        .describe("Values to insert at atIndex (for insertValuesAt)"),
    
    inheritFormattingFrom: z.object({
        seriesIndex: z.number().int().min(0),
        properties: z.array(z.enum([
            "fill", "outline", "line", "marker", "dataLabels", "all"
        ])).optional(),
    }).optional()
        .describe("For INSERT: inherit styling from this series"),
    
    fromIndex: z.number().int().min(0).optional()
        .describe("For MOVE: original series index"),
    
    toIndex: z.number().int().min(0).optional()
        .describe("For MOVE: target series index"),
    
    spacingConsequences: z.string().optional()
        .describe("Impact: 'Adding series increases legend height by ~20pt'"),
    
    shapesAffectedBySpacingConsequences: z.array(z.number().int()).optional()
        .describe("Shape IDs affected by chart/legend size changes"),
}).refine(
    (data) => {
        switch (data.operation) {
            case "insert":
                return data.index !== undefined && data.values !== undefined;
            case "remove":
                return data.index !== undefined;
            case "replace":
            case "setValues":
                return data.index !== undefined && data.values !== undefined;
            case "extendValues":
                return data.index !== undefined && data.appendValues !== undefined && data.appendValues.length > 0;
            case "insertValuesAt":
                return data.index !== undefined && data.atIndex !== undefined && data.insertValues !== undefined && data.insertValues.length > 0;
            case "move":
                return data.fromIndex !== undefined && data.toIndex !== undefined;
            default:
                return true;
        }
    },
    {
        message: "Missing required fields for operation. insert/replace/setValues require index+values, remove requires index, extendValues requires index+appendValues, insertValuesAt requires index+atIndex+insertValues, move requires fromIndex+toIndex",
    }
);

// ═══════════════════════════════════════════════════════════════════════════
// CHART CONFIG SCHEMA WITH OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

// Internal schemas for chart-type specific options (used with styledProperty)
const BarColumnOptionsSchema = z.object({
    gapWidth: z.number().int().min(0).max(500).optional()
        .describe("Gap between bars (0-500, default: 150)"),
    seriesOverlap: z.number().int().min(-100).max(100).optional()
        .describe("Series overlap (-100 to 100, default: 0)"),
});

const PieOptionsSchema = z.object({
    rotation: z.number().int().min(0).max(360).optional()
        .describe("Rotation angle (0-360, default: 0)"),
    holeSize: z.number().int().min(10).max(90).optional()
        .describe("Doughnut hole size (10-90, COM minimum is 10)"),
    explosion: z.number().int().min(0).max(400).optional()
        .describe("Slice explosion (0-400, default: 0)"),
});

const LineOptionsSchema = z.object({
    smoothLines: z.boolean().optional()
        .describe("Use smooth/curved lines (default: false)"),
});

/**
 * Waterfall chart options
 * 
 * Waterfall charts auto-classify points based on value:
 * - Positive values → "Increase" bars (use increaseColor)
 * - Negative values → "Decrease" bars (use decreaseColor)  
 * - Points in totalPoints[] → "Total" bars anchored to zero (use totalColor)
 * 
 * Individual point colors can still be overridden via series_styles[].dataPoints[].fill
 */
const WaterfallOptionsSchema = z.object({
    showConnectorLines: z.boolean().optional()
        .describe("Show connector lines between bars. DEFAULT: false (hidden)"),
    increaseColor: z.string().optional()
        .describe("Color for positive/increase bars (auto-applied based on value > 0)"),
    decreaseColor: z.string().optional()
        .describe("Color for negative/decrease bars (auto-applied based on value < 0)"),
    totalColor: z.string().optional()
        .describe("Color for total/subtotal bars (bars that start from zero)"),
    totalPoints: z.array(z.number().int().min(0)).optional()
        .describe("Indices of points to mark as totals (0-based). These bars anchor to zero baseline instead of floating."),
});

// Internal schemas for legend/title/plotArea (used with styledProperty)
const LegendSchema = z.object({
    position: z.enum(["none", "top", "bottom", "left", "right", "top_right"]).optional(),
    overlay: z.boolean().optional(),
    textFormat: TextFormatSchema.optional(),
});

const TitleSchema = z.object({
    show: z.boolean().optional(),
    text: z.string().optional(),
    textFormat: TextFormatSchema.optional(),
    centered: z.boolean().optional(),
    height: z.number().optional(),
});

const PlotAreaSchema = z.object({
    fill: z.object({
        color: z.string().optional(),
        transparency: z.number().optional(),
    }).optional(),
    outline: z.object({
        show: z.boolean().optional()
            .describe("Show border. DEFAULT: false."),
        color: z.string().optional(),
        weight: z.number().optional(),
        dashStyle: z.enum(["solid", "dash", "dot", "dash_dot", "dash_dot_dot"]).optional()
            .describe("Border dash style"),
    }).optional(),
});

/**
 * Chart config schema with operations (for edit mode)
 * 
 * Style properties support per-property inheritance via styledProperty() wrapper.
 * You can either provide a direct value OR inherit from another chart.
 */
export const ChartConfigWithOperationsSchema = z.object({
    chartType: ChartTypeEnum.optional()
        .describe("Chart type (REQUIRED when operationType='create')"),
    
    // Chart-type specific options - support inheritance
    barColumnOptions: styledProperty(BarColumnOptionsSchema).optional()
        .describe("Bar/column chart options"),
    
    pieOptions: styledProperty(PieOptionsSchema).optional()
        .describe("Pie/doughnut chart options"),
    
    lineOptions: styledProperty(LineOptionsSchema).optional()
        .describe("Line chart options"),
    
    waterfallOptions: styledProperty(WaterfallOptionsSchema).optional()
        .describe("Waterfall chart options: colors for increase/decrease/total bars, connector line visibility"),
    
    // Chart data (for create mode)
    data: z.object({
        categories: z.object({
            type: z.enum(["string", "number", "date", "auto"]).optional(),
            values: z.array(z.union([z.string(), z.number()])).optional(),
            generator: DataGeneratorSchema.optional(),
            formatCode: z.string().optional(),
        }).optional(),
        series: z.array(SeriesSchema).optional(),
    }).optional()
        .describe("Chart data (REQUIRED for create)"),
    
    // Data operations (for edit mode)
    categoryOperations: z.array(CategoryOperationSchema).optional()
        .describe("Category operations: insert, remove, setValues, extendValues"),
    
    seriesOperations: z.array(SeriesOperationSchema).optional()
        .describe("Series operations: insert, remove, replace, move, setValues, extendValues, insertValuesAt"),
    
    // Series styling with per-point overrides
    seriesStyles: z.array(SeriesStyleSchema).optional()
        .describe("Per-series styling with optional dataPoints overrides"),
    
    // Axes - support inheritance
    axes: z.object({
        categoryAxis: styledProperty(AxisSchema).optional(),
        primaryValueAxis: styledProperty(AxisSchema).optional(),
        secondaryValueAxis: styledProperty(AxisSchema).optional(),
    }).optional(),
    
    // Chart elements - support inheritance
    legend: styledProperty(LegendSchema).optional()
        .describe("Legend configuration"),
    
    title: styledProperty(TitleSchema).optional()
        .describe("Chart title configuration"),
    
    plotArea: styledProperty(PlotAreaSchema).optional()
        .describe("Plot area (data region) styling. DEFAULT: transparent, no border."),
    
    chartArea: styledProperty(ChartAreaSchema).optional()
        .describe("Entire chart area styling. DEFAULT: transparent, no border."),
    
    // Bulk inheritance from another chart
    inheritFrom: z.object({
        chartShapeId: z.number().int(),
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        properties: z.array(z.string()).optional(),
        seriesMapping: z.array(z.object({
            sourceIndex: z.number().int(),
            targetIndex: z.number().int(),
        })).optional(),
    }).optional()
        .describe("Inherit multiple properties from another chart"),
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN CREATE OR EDIT CHART PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create Or Edit Chart parameters schema
 */
export const CreateOrEditChartParamsSchema = z.object({
    slideId: z.number().int().min(1)
        .describe("1-based slide index"),
    
    operationType: z.enum(["create", "edit"])
        .describe("'create' for new chart, 'edit' for existing chart"),
    
    shapeId: z.number().int().optional()
        .describe("Chart shape ID (REQUIRED when operationType='edit')"),
    
    purpose: z.string().optional()
        .describe("Documentation: what this chart is for"),
    
    notes: z.string().optional()
        .describe("Additional notes"),
    
    // Position & Transform
    position: PositionSchema.optional()
        .describe("Chart position and dimensions"),
    
    rotation: z.number().optional()
        .describe("Rotation in degrees clockwise"),
    
    zOrder: z.number().int().optional()
        .describe("Stacking order (higher = on top)"),
    
    // Styling (optional for charts, NO shadow)
    fill: FillSchema.optional()
        .describe("Chart background fill (optional)"),
    
    outline: OutlineSchema.optional()
        .describe("Chart outline (optional)"),
    
    // NOTE: Charts don't have shadow
    
    // Chart config
    chartConfig: ChartConfigWithOperationsSchema
        .describe("Chart configuration with optional operations"),
    
    // Metadata (edit mode)
    metadata: MetadataOperationsSchema.optional()
        .describe("Unified metadata (edit mode: removeEntryIds + addEntries)"),
}).refine(
    (data) => {
        // chartType and data required for create
        if (data.operationType === "create") {
            if (!data.chartConfig.chartType || !data.chartConfig.data) {
                return false;
            }
        }
        // shapeId required for edit
        if (data.operationType === "edit" && !data.shapeId) {
            return false;
        }
        return true;
    },
    {
        message: "chartType/data required for create mode, shapeId required for edit mode",
    }
);

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type CreateOrEditChartParams = z.infer<typeof CreateOrEditChartParamsSchema>;
export type ChartConfigWithOperations = z.infer<typeof ChartConfigWithOperationsSchema>;
export type CategoryOperation = z.infer<typeof CategoryOperationSchema>;
export type SeriesOperation = z.infer<typeof SeriesOperationSchema>;

// New type exports
export type InheritFromRef = z.infer<typeof InheritFromRefSchema>;
export type TextFormat = z.infer<typeof TextFormatSchema>;
export type MarkerStyle = z.infer<typeof MarkerStyleEnum>;
export type MarkerConfig = z.infer<typeof MarkerSchema>;
export type DataLabel = z.infer<typeof DataLabelSchema>;
export type DataPointOverride = z.infer<typeof DataPointOverrideSchema>;
export type ChartAreaConfig = z.infer<typeof ChartAreaSchema>;
export type SeriesStyle = z.infer<typeof SeriesStyleSchema>;

